import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { CalendarDays, Video, Users, Clock } from 'lucide-react';

const KIND = {
  session: { icon: Users, label: 'Session', accent: 'border-cyan-300/25 bg-cyan-400/[0.06]' },
  stream: { icon: Video, label: 'Live stream', accent: 'border-violet-300/25 bg-violet-400/[0.06]' },
  community: { icon: CalendarDays, label: 'Community', accent: 'border-white/10 bg-white/[0.03]' },
};

const when = (at) => {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return at;
  return date.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const Item = ({ item, dim }) => {
  const kind = KIND[item.kind] || KIND.community;
  const Icon = kind.icon;
  return (
    <Link
      to={item.link}
      className={`flex items-start justify-between gap-3 rounded-xl border px-4 py-3
                  ${kind.accent} ${dim ? 'opacity-60' : ''}
                  hover:brightness-125 transition-all duration-200`}
      data-testid={`calendar-item-${item.id}`}
    >
      <span className="flex items-start gap-3 min-w-0">
        <Icon className="w-4 h-4 text-white/60 shrink-0 mt-0.5" />
        <span className="min-w-0">
          <span className="block text-sm text-white/90 truncate">{item.title}</span>
          <span className="block text-xs text-white/45 mt-0.5 truncate">
            {when(item.at)}
            {item.with ? ` · with ${item.with}` : ''}
            {item.minutes ? ` · ${item.minutes} min` : ''}
          </span>
        </span>
      </span>
      <span className="text-[11px] text-white/40 shrink-0 capitalize">
        {item.status === 'open' ? kind.label : item.status}
      </span>
    </Link>
  );
};

/** Their diary: sessions booked, streams they can join, and the collective's own dates. */
export const MyCalendar = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/companion/calendar`)
      .then((r) => setData(r.data))
      .catch(() => setData({ upcoming: [], past: [] }));
  }, []);

  if (!data) {
    return <p className="text-sm text-white/50" data-testid="calendar-loading">Opening your calendar…</p>;
  }

  return (
    <div className="space-y-10" data-testid="my-calendar">
      <section className="space-y-3" data-testid="calendar-upcoming">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-white">What&apos;s coming</h2>
          <p className="text-sm text-white/45">
            Your sessions, the live gatherings and the community calendar, in one line of time.
          </p>
        </div>
        {data.upcoming.length ? (
          <div className="space-y-2">
            {data.upcoming.map((item) => <Item key={`${item.kind}-${item.id}`} item={item} />)}
          </div>
        ) : (
          <p className="text-sm text-white/45" data-testid="calendar-upcoming-empty">
            Nothing in the diary yet.{' '}
            <Link to="/dashboard/bookings" className="text-cyan-300 hover:text-cyan-200"
                  data-testid="calendar-book-link">Book a session</Link>
            {' '}or{' '}
            <Link to="/dashboard/live-stream" className="text-cyan-300 hover:text-cyan-200"
                  data-testid="calendar-stream-link">see the next live gathering</Link>.
          </p>
        )}
      </section>

      <section className="space-y-3" data-testid="calendar-past">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-white">Already happened</h2>
          <p className="text-sm text-white/45 inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> The last twenty-five.
          </p>
        </div>
        {data.past.length ? (
          <div className="space-y-2">
            {data.past.map((item) => <Item key={`${item.kind}-${item.id}`} item={item} dim />)}
          </div>
        ) : (
          <p className="text-sm text-white/45" data-testid="calendar-past-empty">
            Nothing behind you yet — this fills as you go.
          </p>
        )}
      </section>
    </div>
  );
};

export default MyCalendar;
